import { AionzDialog, AionzSnackbar, AionzTooltip, DialogSize, LoadingService } from '@aionzdev/design-system';
import { CurrencyPipe, DatePipe, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
  viewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import {
  auditTime,
  catchError,
  concatMap,
  finalize,
  forkJoin,
  from,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  throwError
} from 'rxjs';
import { WebsocketService } from '@aionzdev/modules';
import { LoadState, PurchaseRequestStatus } from '../../../enums';
import { AddSupplierToProductDto } from '../../../interfaces/add-supplier-to-product.dto';
import { ApproveQuotationMap, ApproveQuotationMapPayload } from '../../../interfaces/approve-quotation-map';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { QuotationMapBoardData } from '../../../interfaces/quotation-map-board-data';
import { QuotationMapData } from '../../../interfaces/quotation-map-data';
import { QuotationMapSupplierStatus } from '../../../interfaces/quotation-map-supplier';
import { PurchaseSupplier, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { TradingMatchSuggestion } from '../../../interfaces/trading-match-suggestion';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { QuotationMapPreferencesService } from '../../../services/quotation-map-preferences.service';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { BranchesTooltipComponent } from '../../atoms/branches-tooltip/branches-tooltip.component';
import { NewSupplierSaveEvent } from '../../components/new-supplier-dialog/new-supplier-dialog.component';
import { BranchesSummary } from '../../../interfaces/branches-summary';
import { deliveryCityLine, injectBranchCities } from '../../utils/branch-label.util';
import { QuotationMapBoardComponent } from '../../components/quotation-map-board/quotation-map-board.component';
import { PurchaseRequestActivityPanelComponent } from '../../components/purchase-request-activity-panel/purchase-request-activity-panel.component';
import { PurchaseRequestAttachmentsComponent } from '../../components/purchase-request-attachments/purchase-request-attachments.component';
import { QuotationMapBoardSkeletonComponent } from '../../components/skeleton/quotation-map-board-skeleton/quotation-map-board-skeleton.component';
import { QuotationMapHeaderSkeletonComponent } from '../../components/skeleton/quotation-map-header-skeleton/quotation-map-header-skeleton.component';
import { QuotationMapMetaSkeletonComponent } from '../../components/skeleton/quotation-map-meta-skeleton/quotation-map-meta-skeleton.component';
import { TradingMatchParceriaDialogComponent } from '../../components/trading-match-parceria-dialog/trading-match-parceria-dialog.component';

/** Tipo de evento realtime da busca de fornecedores sugeridos (Cotação Inteligente). */
const SUPPLIER_SUGGESTION_EVENT_TYPE = 'fornecedor_sugerido';

@Component({
  selector: 'aionz-quotation-map',
  imports: [
    QuotationMapBoardComponent,
    QuotationMapBoardSkeletonComponent,
    QuotationMapHeaderSkeletonComponent,
    QuotationMapMetaSkeletonComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
    AionzTooltip,
    CurrencyPipe,
    DatePipe,
    PurchaseRequestStatusComponent,
    PurchaseRequestActivityPanelComponent,
    PurchaseRequestAttachmentsComponent,
    MatTabsModule,
    MatButtonToggleModule
  ],
  templateUrl: './quotation-map.component.html',
  styleUrl: './quotation-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationMapComponent implements OnDestroy {
  protected activityPanel = viewChild.required(PurchaseRequestActivityPanelComponent);
  protected quotationMapBoard = viewChild(QuotationMapBoardComponent);

  protected readonly branchesTooltipComponent = BranchesTooltipComponent;

  /** Locais de entrega do mapa, no formato do tooltip do chip de locais do quadro. */
  /** Com mais de um local, o tooltip do chip repete as cores dos pins e serve de legenda. */
  private readonly branchCities = injectBranchCities();
  protected readonly deliveryBranchesSummary = computed<BranchesSummary>(() => {
    const board = this.quotationMapBoard();
    const colors = board?.branchColors();
    return {
      branches: (board?.deliveryBranches() ?? []).map(name => {
        // o endereço vem do primeiro item daquele local
        const item = this.data().items.find(current => current.branch === name);
        return {
          id: name,
          name,
          street: item?.branchStreet,
          city: item ? deliveryCityLine(item, this.branchCities()) : undefined,
          color: colors?.get(name),
        };
      }),
    };
  });

  protected readonly deliveryBranchesLabel = computed(() => {
    const n = this.deliveryBranchesSummary().branches.length;
    return `${n} ${n === 1 ? 'local de entrega' : 'locais de entrega'}`;
  });
  data = signal<QuotationMapData>({
    description: '',
    id: '',
    items: [],
    requestedAt: new Date(),
    status: PurchaseRequestStatus.IN_QUOTATION,
    supplierIds: [],
    supplierFreights: new Map(),
    title: ''
  });

  protected selectedTabIndex = signal(0);
  protected loadState = signal<LoadState>(LoadState.LOADING);

  /**
   * Quantidade de respostas de busca de fornecedores sugeridos ainda
   * pendentes (uma por produto enfileirado). `null` = nenhuma busca em
   * andamento. Usado só para controlar o loading do botão nesta sessão —
   * não persiste em F5/saída de página.
   */
  private readonly pendingSupplierSuggestionResponses = signal<number | null>(null);
  /** Produtos enfileirados na busca atual e quantos já responderam com fornecedor novo. */
  private readonly supplierSearchTotal = signal<number | null>(null);
  private readonly supplierSearchFound = signal(0);

  /** Progresso "3 de 15" para o botão de busca no quadro; null fora de uma busca. */
  protected readonly supplierSearchProgress = computed<{ done: number; total: number } | null>(() => {
    const total = this.supplierSearchTotal();
    const pending = this.pendingSupplierSuggestionResponses();
    if (total === null || pending === null) return null;
    return { done: Math.max(0, total - pending), total };
  });
  protected readonly isSearchingSuppliers = computed(
    () => this.pendingSupplierSuggestionResponses() !== null
  );

  quotationMapBoardData = computed<QuotationMapBoardData>(() => ({
    purchaseId: this.data().id,
    items: this.data().items,
    supplierIds: this.data().supplierIds,
    supplierFreights: this.data().supplierFreights ?? new Map(),
    totalSelected: 0,
    totalValue: 0
  }));

  private iconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private purchaseService = inject(AbstractPurchasingService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBar = inject(AionzSnackbar);
  private _loading = inject(LoadingService);
  private _websocketService = inject(WebsocketService);
  private dialog = inject(AionzDialog);
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();
  private quotationRealtimeDestroy$ = new Subject<void>();
  private _websocketUpdate$ = new Subject<PurchasingEvent>();

  private isRefreshing = false;
  private currentPurchaseId?: string;
  private readonly preferences = inject(QuotationMapPreferencesService);

  /**
   * Garante que o dialog de sugestões de produtos de vendedores parceiros só é
   * aberto automaticamente uma vez por `purchaseId` nesta sessão de página
   * (evita reabrir a cada `loadQuotationMap()` disparado por realtime).
   */
  private dialogShownForPurchaseId?: string;

  constructor() {
    this.iconRegistry.addSvgIcon(
      'calendar_month',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/calendar_month.svg')
    );
    this.iconRegistry.addSvgIcon(
      'earnings',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/earnings.svg')
    );

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentPurchaseId = params['purchaseId'];
      // URL manda (link compartilhado abre na aba certa); sem parâmetro, vale a última aba usada aqui
      const abaNaUrl = this.route.snapshot.queryParamMap.get('aba');
      const tabFromUrl = abaNaUrl === 'anexos' ? 1 : abaNaUrl === 'quadro' ? 0 : null;
      this.selectedTabIndex.set(tabFromUrl ?? this.preferences.get(this.currentPurchaseId).tabIndex ?? 0);
      this.loadQuotationMap();

      if (isPlatformBrowser(this.platformId)) {
        this.setupRealtimeUpdates();
      }
    });
  }

  protected onTabChange(index: number): void {
    this.selectedTabIndex.set(index);
    this.preferences.patch(this.currentPurchaseId, { tabIndex: index });
    // reflete na URL sem criar entrada no histórico; "quadro" é o padrão e sai da URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { aba: index === 1 ? 'anexos' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * @param silent atualização em segundo plano (tempo real): mantém o quadro na tela,
   *               sem skeleton, e um erro só avisa, sem trocar o estado da página.
   */
  private loadQuotationMap(silent = false): void {
    if (!this.currentPurchaseId || this.isRefreshing) return;

    this.isRefreshing = true;
    if (!silent) this.loadState.set(LoadState.LOADING);

    this.purchaseService
      .getQuotationMapBoardData(this.currentPurchaseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: res => {
        this.isRefreshing = false;
        this.loadState.set(LoadState.LOADED);

        if (!res.body || res.body.status !== PurchaseRequestStatus.IN_QUOTATION) {
          this.snackBar.warning('Esta oferta não está em cotação');
          this.router.navigate(['../mapa-de-cotacao', this.currentPurchaseId], {
            relativeTo: this.route
          });
          return;
        }

        res.body && this.data.set(res.body);
        this.loadTradingMatchSuggestions();
      },
      error: err => {
        this.isRefreshing = false;

        let message = 'Erro ao buscar a oferta!';
        if (err instanceof HttpErrorResponse && err.status === 404) {
          message = 'Oferta não encontrada!';
        }

        if (silent) {
          this.snackBar.error('Não foi possível atualizar o mapa. Os dados exibidos podem estar desatualizados.');
          return;
        }

        this.loadState.set(LoadState.ERROR);
        this.snackBar.error(message);
        this.router.navigate(['../mapa-de-cotacao', this.currentPurchaseId], {
          relativeTo: this.route
        });
      }
    });
  }

  private setupRealtimeUpdates(): void {
    this.quotationRealtimeDestroy$.next();

    if (!this.currentPurchaseId) return;

    const config = this.purchaseService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.quotationMap);

    this._websocketUpdate$
      .pipe(auditTime(500), takeUntil(this.quotationRealtimeDestroy$), takeUntil(this.destroy$))
      .subscribe(event => {
        // Atualização em tempo real não passa pelo skeleton: o quadro recebe os dados novos
        // no lugar e preserva seleções, rascunho e colunas recolhidas.
        this.loadQuotationMap(true);

        if (event.type === SUPPLIER_SUGGESTION_EVENT_TYPE) {
          this.registerSupplierSuggestionResponse(event.encontrouFornecedor === true);
        }
      });

    this._websocketService.sendMessage(config.joinEvent, config.tenantId);

    this._websocketService.listen(config.eventName, (event: any) => {
      const mappedEvent: PurchasingEvent = this.purchaseService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        mappedEvent.purchaseId === this.currentPurchaseId &&
        updateTypes.has(mappedEvent.type)
      ) {
        this._websocketUpdate$.next(mappedEvent);
      }
    });
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      const config = this.purchaseService.getRealtimeConfig();
      this._websocketService.sendMessage(config.leaveEvent, config.tenantId);
    }
    this.quotationRealtimeDestroy$.next();
    this.quotationRealtimeDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cada evento `fornecedor_sugerido` recebido corresponde à resposta de UM
   * produto enfileirado na busca (a fila do backend processa item a item).
   * O evento não identifica qual produto respondeu, então só dá para contar
   * quantas respostas já chegaram, não riscar produtos específicos — chega
   * a 0 pendentes quando todas as N respostas dos produtos enfileirados
   * tiverem chegado, desligando o loading do botão.
   */
  private registerSupplierSuggestionResponse(foundSupplier: boolean): void {
    const pending = this.pendingSupplierSuggestionResponses();
    if (pending === null) return;

    if (foundSupplier) this.supplierSearchFound.update(n => n + 1);

    const remaining = pending - 1;
    if (remaining > 0) {
      this.pendingSupplierSuggestionResponses.set(remaining);
      return;
    }

    // Última resposta: um único resumo em vez de um aviso por produto.
    const total = this.supplierSearchTotal() ?? 0;
    const found = this.supplierSearchFound();
    const produtos = total === 1 ? 'produto' : 'produtos';
    if (found > 0) {
      this.snackBar.success(`Busca concluída: fornecedores novos em ${found} de ${total} ${produtos}.`, undefined, {
        duration: 6000
      });
    } else {
      this.snackBar.info(`Busca concluída: nenhum fornecedor novo nos ${total} ${produtos}.`, undefined, {
        duration: 6000
      });
    }
    this.pendingSupplierSuggestionResponses.set(null);
    this.supplierSearchTotal.set(null);
    this.supplierSearchFound.set(0);
  }

  protected onApprove(data: ApproveQuotationMapPayload): void {
    const approveMap = this.approveQuotationMapMapper(data);
    if (approveMap.items.length === 0) {
      this.snackBar.warning('Selecione ao menos um preço para aprovar a cotação');
      return;
    }

    const justificationText = data.justification?.trim();
    const purchaseId = this.data().id;

    this._loading.showLoading();

    // Contrato com o backend: o PDF do mapa lê a justificativa deste comentário pelo prefixo
    // exato (`PREFIXO_JUSTIFICATIVA_APROVACAO`, ebarn-node, GerarMapaCotacao.ts) e só considera
    // comentários gravados antes da aprovação. Por isso o prefixo não pode mudar sem mudar lá,
    // e o comentário continua sendo gravado ANTES de `approveQuotationMap`.
    const saveJustification$ = justificationText
      ? this.purchaseService
          .createPurchaseRequestComment(
            purchaseId,
            `Justificativa de aprovação: ${justificationText}`
          )
          .pipe(catchError(() => of(null)))
      : of(null);

    saveJustification$
      .pipe(
        switchMap(() => this.purchaseService.approveQuotationMap(approveMap)),
        finalize(() => {
          this._loading.hideLoading();
          this.router.navigate(['../../quadro'], { relativeTo: this.route });
        })
      )
      .subscribe(() => this.snackBar.success('Pedidos aprovados com sucesso'));
  }

  protected onSendRequestEmail(data: { supplierId: string }): void {
    this._loading.showLoading();
    this.purchaseService
      .resendQuotationRequest(data.supplierId, this.data().id)
      .pipe(finalize(() => this._loading.hideLoading()))
      .subscribe({
        next: () => {
          this.snackBar.success('E-mail enviado com sucesso');
        },
        error: () => {
          this.snackBar.error('Erro ao enviar e-mail');
        }
      });
  }

  /** O carregamento fica no botão do dialog (sem o loading de tela inteira por cima dele). */
  protected onCreateSupplier({ supplier, productIds, progress, done }: NewSupplierSaveEvent): void {
    const currentData = this.data();
    if (!currentData.id) {
      done(false);
      return;
    }

    if (productIds) {
      this.createSupplierForProducts(currentData.id, supplier, productIds, progress, done);
      return;
    }

    this.purchaseService
      .createSupplier({
        ...supplier,
        purchaseId: currentData.id
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const createdSupplier = response.body || supplier;
          this.addSupplierToQuotationMap(createdSupplier);
          this.snackBar.success('Fornecedor adicionado à cotação.');
          this.activityPanel().reloadActivities();
          done(true);
        },
        error: (error: HttpErrorResponse) => {
          done(false);
          if (error.status === 409) {
            this.snackBar.warning('Este fornecedor já está nesta cotação.');
            return;
          }

          this.snackBar.error('Não foi possível adicionar o fornecedor. Tente novamente.');
        }
      });
  }

  protected handleTriggerSupplierSuggestionSearch(): void {
    if (this.isSearchingSuppliers()) return;

    // Loading do botão liga já no clique (feedback imediato) e só desliga
    // quando as N respostas dos produtos enfileirados chegarem via
    // WebSocket — ou aqui mesmo, se a chamada falhar ou não enfileirar nada.
    this.pendingSupplierSuggestionResponses.set(0);
    this.supplierSearchTotal.set(null);
    this.supplierSearchFound.set(0);

    this.purchaseService
      .triggerSupplierSuggestionSearch(this.data().id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const result = response.body;
          const emCooldown = result?.produtosEmCooldown?.length ?? 0;
          const enfileirados = result?.produtosEnfileirados?.length ?? 0;

          if (enfileirados > 0) {
            this.supplierSearchTotal.set(enfileirados);
            this.pendingSupplierSuggestionResponses.set(enfileirados);
            this.snackBar.success('Nova busca de fornecedores sugeridos iniciada com sucesso!', undefined, {
              duration: 3000
            });
          } else {
            this.pendingSupplierSuggestionResponses.set(null);
          }

          if (emCooldown > 0) {
            this.snackBar.info(
              enfileirados > 0
                ? `${emCooldown} ${emCooldown === 1 ? 'produto está' : 'produtos estão'} em período de espera e não ${emCooldown === 1 ? 'foi incluído' : 'foram incluídos'} nesta busca.`
                : 'Todos os produtos estão em período de espera. Tente novamente mais tarde.'
            );
          }

          if (enfileirados === 0 && emCooldown === 0) {
            this.snackBar.info('Nenhum produto disponível para busca de fornecedores sugeridos no momento.');
          }

          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.pendingSupplierSuggestionResponses.set(null);
          this.snackBar.error('Erro ao disparar a busca de fornecedores sugeridos.');
        }
      });
  }

  protected onUpdateSuggestedSupplierStatus(data: {
    supplierId: string;
    productIds: string[];
    status: PurchaseSupplierLinkStatus.APROVADO | PurchaseSupplierLinkStatus.REJEITADO;
  }): void {
    this._loading.showLoading();
    forkJoin(
      data.productIds.map(productId =>
        this.purchaseService.updateSuggestedSupplierStatus({
          purchaseId: this.data().id,
          productId,
          supplierId: data.supplierId,
          status: data.status
        })
      )
    )
      .pipe(finalize(() => this._loading.hideLoading()))
      .subscribe({
        next: () => {
          this.snackBar.success(
            data.status === PurchaseSupplierLinkStatus.APROVADO
              ? 'Fornecedor aceito com sucesso!'
              : 'Fornecedor recusado com sucesso!'
          );
          this.activityPanel().reloadActivities();
          this.loadQuotationMap(true);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.snackBar.warning('Este fornecedor sugerido já foi avaliado.');
          } else if (error.status === 404) {
            this.snackBar.warning('Fornecedor sugerido não encontrado.');
          } else {
            this.snackBar.error('Erro ao atualizar o status do fornecedor sugerido.');
          }
        }
      });
  }

  /**
   * Busca sugestões PENDENTES de produtos de vendedores parceiros (match com
   * Trading pública feito por job de backend) e, havendo alguma,
   * abre o dialog de revisão automaticamente — só uma vez por `purchaseId`
   * nesta sessão de página, mesmo que `loadQuotationMap()` seja chamado de
   * novo por realtime.
   */
  private loadTradingMatchSuggestions(): void {
    // No SSR o dialog seria aberto no servidor e serializado no HTML, e o
    // navegador abriria outro na hidratação (dialog duplicado).
    if (!isPlatformBrowser(this.platformId)) return;

    const purchaseId = this.data().id;
    if (!purchaseId) return;

    forkJoin({
      suggestions: this.purchaseService.getTradingMatchSuggestions(purchaseId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ suggestions }) => {
        const pendingSuggestions = suggestions.body ?? [];

        if (pendingSuggestions.length > 0 && this.dialogShownForPurchaseId !== purchaseId) {
          this.dialogShownForPurchaseId = purchaseId;
          this.openTradingMatchParceriaDialog(pendingSuggestions);
        }
      });
  }

  private openTradingMatchParceriaDialog(suggestions: TradingMatchSuggestion[]): void {
    const dialogRef = this.dialog.open<TradingMatchParceriaDialogComponent, TradingMatchSuggestion[]>(
      TradingMatchParceriaDialogComponent,
      {
        size: DialogSize.LG,
        data: suggestions
      }
    );

    const approveSubscription = dialogRef.componentInstance.approveSuggestion.subscribe(
      (suggestion: TradingMatchSuggestion) =>
        this.updateTradingMatchSuggestionStatus(suggestion, PurchaseSupplierLinkStatus.APROVADO)
    );
    const rejectSubscription = dialogRef.componentInstance.rejectSuggestion.subscribe(
      (suggestion: TradingMatchSuggestion) =>
        this.updateTradingMatchSuggestionStatus(suggestion, PurchaseSupplierLinkStatus.REJEITADO)
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        approveSubscription.unsubscribe();
        rejectSubscription.unsubscribe();
      });
  }

  private updateTradingMatchSuggestionStatus(
    suggestion: TradingMatchSuggestion,
    status: PurchaseSupplierLinkStatus.APROVADO | PurchaseSupplierLinkStatus.REJEITADO
  ): void {
    this._loading.showLoading();
    this.purchaseService
      .updateTradingMatchSuggestionStatus({
        purchaseId: this.data().id,
        matchId: suggestion.matchId,
        status
      })
      .pipe(finalize(() => this._loading.hideLoading()))
      .subscribe({
        next: () => {
          this.snackBar.success(
            status === PurchaseSupplierLinkStatus.APROVADO
              ? 'Sugestão de vendedor parceiro aprovada com sucesso!'
              : 'Sugestão de vendedor parceiro rejeitada.'
          );
          this.loadQuotationMap(true);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.snackBar.warning('Sugestão de vendedor parceiro não encontrada.');
          } else {
            this.snackBar.error('Erro ao atualizar a sugestão de vendedor parceiro.');
          }
        }
      });
  }

  protected onDeleteSupplier(data: { supplierId: string }): void {
    this._loading.showLoading();
    this.purchaseService
      .deleteSupplier(data.supplierId, this.data().id)
      .pipe(
        finalize(() => this._loading.hideLoading()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.removeSupplierFromQuotationMap(data.supplierId);
          this.snackBar.success('Fornecedor excluído com sucesso!');
          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.snackBar.error('Erro ao excluir fornecedor.');
        }
      });
  }

  protected onAddSupplierToProduct(data: AddSupplierToProductDto): void {
    this._loading.showLoading();
    this.purchaseService
      .addSupplierToProduct(data)
      .pipe(
        finalize(() => this._loading.hideLoading()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.snackBar.success('Fornecedor adicionado à cotação do produto com sucesso!');
          this.loadQuotationMap(true);
          this.activityPanel().reloadActivities();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.snackBar.warning('Este fornecedor já está participando da cotação deste produto.');
            return;
          }
          this.snackBar.error('Erro ao adicionar fornecedor à cotação do produto.');
        }
      });
  }

  protected handleGoBack(): void {
    this.router.navigate(['../../quadro'], { relativeTo: this.route });
  }

  protected toggleSidebar(): void {
    this.activityPanel().toggle();
  }

  protected openActivityTab(): void {
    this.activityPanel().openTab(0);
  }

  protected openCommentsTab(): void {
    this.activityPanel().openTab(1);
  }

  private approveQuotationMapMapper(payload: ApproveQuotationMapPayload): ApproveQuotationMap {
    return {
      purchaseId: this.data().id,
      negotiatedFreights: payload.negotiatedFreights,
      items: payload.items.flatMap(item => {
        const selectedSuppliers = Array.from(item.suppliers.values()).filter(
          supplier => supplier.negotiatedSelected || supplier.selected
        );

        return selectedSuppliers.map(supplier => ({
          offerId: supplier.offerId,
          price: supplier.negotiatedSelected
            ? (supplier.negotiatedPrice ?? supplier.price)
            : supplier.price
        }));
      })
    };
  }

  /**
   * Convida o fornecedor só para os itens escolhidos: uma chamada por produto, em sequência. O
   * backend reaproveita o vínculo com a oferta e manda um único convite; 409 = já estava no item,
   * conta como feito. No fim recarrega o mapa (os demais itens ficam "Não convidado").
   */
  private createSupplierForProducts(
    purchaseId: string,
    supplier: PurchaseSupplier,
    productIds: string[],
    progress: (done: number, total: number) => void,
    done: (ok: boolean) => void
  ): void {
    const total = productIds.length;
    let added = 0;
    let alreadyThere = 0;
    progress(0, total);

    from(productIds)
      .pipe(
        concatMap(productId =>
          this.purchaseService.createSupplierForProduct({ ...supplier, purchaseId, productId }, { hideGlobalError: true }).pipe(
            map(() => true),
            catchError((error: HttpErrorResponse) =>
              error.status === 409 ? of(false) : throwError(() => error)
            )
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: created => {
          if (!created) alreadyThere++;
          progress(++added, total);
        },
        complete: () => {
          this.loadQuotationMap(true);
          this.activityPanel().reloadActivities();
          const invited = total - alreadyThere;
          const itens = (n: number) => `${n} ${n === 1 ? 'item' : 'itens'}`;
          this.snackBar.success(
            invited === 0
              ? 'Este fornecedor já estava nos itens escolhidos.'
              : `Fornecedor convidado para ${itens(invited)}` +
                  (alreadyThere > 0 ? ` (${alreadyThere} já ${alreadyThere === 1 ? 'tinha' : 'tinham'} este fornecedor).` : '.')
          );
          done(true);
        },
        error: () => {
          // Os itens que já entraram continuam valendo; tentar de novo só completa o restante.
          if (added > 0) this.loadQuotationMap(true);
          this.snackBar.error(
            added > 0
              ? `Adicionado em ${added} de ${total} itens. Tente de novo para completar.`
              : 'Não foi possível adicionar o fornecedor. Tente novamente.'
          );
          done(false);
        }
      });
  }

  private addSupplierToQuotationMap(supplier: PurchaseSupplier): void {
    const supplierId = supplier.id || supplier.cnpj;
    const supplierName = supplier.fantasyName || supplier.name;
    const currentData = this.data();

    const supplierFreights = new Map(currentData.supplierFreights ?? []);
    if (!supplierFreights.has(supplierId)) {
      supplierFreights.set(supplierId, {
        supplierId,
        freight: 0,
        selected: false,
        negotiatedFreight: null,
        negotiatedSelected: false
      });
    }

    this.data.set({
      ...currentData,
      supplierIds: currentData.supplierIds.includes(supplierId)
        ? currentData.supplierIds
        : [...currentData.supplierIds, supplierId],
      supplierFreights,
      items: currentData.items.map(item => {
        const suppliers = new Map(item.suppliers);
        suppliers.set(supplierId, {
          id: supplierId,
          offerId: '',
          companyName: supplierName,
          status: QuotationMapSupplierStatus.PENDING,
          respondedAt: '',
          validity: '',
          price: 0,
          selected: false,
          negotiatedPrice: null,
          negotiatedSelected: false,
          bestPrice: false,
          observations: '',
          paymentMethod: '',
          paymentTerms: '',
          email: supplier.email
        });

        return { ...item, suppliers };
      })
    });
  }

  private removeSupplierFromQuotationMap(supplierId: string): void {
    const currentData = this.data();

    const supplierFreights = new Map(currentData.supplierFreights ?? []);
    supplierFreights.delete(supplierId);

    this.data.set({
      ...currentData,
      supplierIds: currentData.supplierIds.filter(
        currentSupplierId => currentSupplierId !== supplierId
      ),
      supplierFreights,
      items: currentData.items.map(item => {
        const suppliers = new Map(item.suppliers);
        suppliers.delete(supplierId);

        return { ...item, suppliers };
      })
    });
  }
}
